import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import "../../utils/axiosInterceptor";
import "./styles.css";
import Loader from "react-loader-spinner";
import CertificationModern from "../CertificationModern";
import { QRCodeSVG } from "qrcode.react";
import { jsPDF } from "jspdf";
import { svg2pdf } from "svg2pdf.js";
import certificationBackground from "../../images/certificationBackground.jpg";

// ---------- Helpers de carregamento de imagem ----------

// Carrega via <img>. useCors=true para imagens remotas (S3/CDN).
function loadViaImg(src, useCors) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCors) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Falha no <img>: " + src));
    img.src = src;
  });
}

// Junta um parâmetro à URL respeitando query string já existente.
function joinParam(url, kv) {
  if (!kv) return url;
  return url + (url.includes("?") ? "&" : "?") + kv;
}

// Carrega via fetch -> blob -> dataURL. Depois de virar dataURL,
// a leitura no canvas nunca é bloqueada por CORS.
async function loadViaFetch(src) {
  const resp = await fetch(src, { mode: "cors", credentials: "omit" });
  if (!resp.ok) throw new Error("HTTP " + resp.status);
  const blob = await resp.blob();
  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = () => rej(new Error("FileReader falhou"));
    fr.readAsDataURL(blob);
  });
  return loadViaImg(dataUrl, false);
}

// Carrega imagem REMOTA resistente a CORS/URL malformada:
// tenta <img crossOrigin> (com e sem ?x-request=html) e, por fim, fetch->blob.
async function loadRemoteImage(baseUrl) {
  const variants = [joinParam(baseUrl, "x-request=html"), baseUrl];
  for (const src of variants) {
    try {
      return await loadViaImg(src, true);
    } catch (_) {
      /* tenta a próxima estratégia */
    }
  }
  for (const src of variants) {
    try {
      return await loadViaFetch(src);
    } catch (_) {
      /* tenta a próxima estratégia */
    }
  }
  throw new Error("Falha ao carregar imagem remota: " + baseUrl);
}

// Encaixa a imagem numa caixa preservando a proporção (object-fit: contain)
function drawFitted(doc, img, bx, by, bw, bh, fmt = "PNG") {
  const ar = img.naturalWidth / img.naturalHeight;
  let w = bw;
  let h = bw / ar;
  if (h > bh) {
    h = bh;
    w = bh * ar;
  }
  const x = bx + (bw - w) / 2;
  const y = by + (bh - h) / 2;
  doc.addImage(img, fmt, x, y, w, h);
  return { x, y, w, h };
}

// ---------- Cores ----------
const C_MAROON = [84, 0, 10]; // #54000A
const C_BODY = [40, 40, 40];
const C_TEXT_GRAY = [30, 30, 30];
const C_SIGN = [51, 51, 51];

export default function Certification() {
  // O token opaco vem da rota /certificado/:public_token.
  // Nao ha mais jwt.decode, nem user_id/course_id na URL.
  const { public_token } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [topics, setTopics] = useState([]);
  const [leftTopics, setLeftTopics] = useState([]);
  const [rightTopics, setRightTopics] = useState([]);

  const url = `${window.location.origin}/certificado/${public_token}`;

  function divideTopics(topicsList) {
    let leftTopicsAux = [];
    let rightTopicsAux = [];
    let counter = 0;
    let lineCounter = 0;
    while (lineCounter < 19 && counter < topicsList.length) {
      const newLineCounter =
        lineCounter + Math.floor((topicsList[counter].length - 1) / 51) + 1;
      if (newLineCounter <= 19) {
        leftTopicsAux.push(topicsList[counter]);
        lineCounter = newLineCounter;
      } else {
        leftTopicsAux.push(
          topicsList[counter].slice(0, (19 - lineCounter) * 51 + 1),
        );
        rightTopicsAux.push(
          topicsList[counter].slice(
            (19 - lineCounter) * 51 + 1,
            topicsList[counter].length,
          ),
        );
        lineCounter = 19;
      }
      counter++;
    }
    for (let i = counter; i < topicsList.length; i++) {
      rightTopicsAux.push(topicsList[i]);
    }
    setLeftTopics(leftTopicsAux);
    setRightTopics(rightTopicsAux);
  }

  //formata a data de YYYY-MM-DDT00:00:00.000Z para DD/MM/YYYY
  function formatDate(date) {
    if (!date) return "";
    const [year, month, day] = date.split("T")[0].split("-");
    return day + "/" + month + "/" + year;
  }

  useEffect(() => {
    divideTopics(topics);
    // eslint-disable-next-line
  }, [topics]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage("");

    api
      .get(`/certificate/${public_token}`)
      .then((response) => {
        if (cancelled) return;
        setData(response.data);
        const courseTopics = response.data.course.topics;
        if (courseTopics) {
          setTopics(
            "- ".concat(courseTopics.replace(/\n/g, "\n- ")).split("\n"),
          );
        } else {
          setTopics([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        const status = error.response ? error.response.status : null;
        if (status === 403) {
          setErrorMessage(
            "Este certificado nao esta disponivel no momento. Procure a secretaria do Centro Academico.",
          );
        } else {
          setErrorMessage("Certificado nao encontrado ou link invalido.");
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [public_token]);

  async function printDocument() {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const W = doc.internal.pageSize.getWidth(); // 297
      const cx = W / 2;

      const s = signatures || [];

      // ---- Marca d'água (asset local, mesma origem) ----
      let bgImg = null;
      try {
        bgImg = await loadViaImg(certificationBackground, false);
      } catch (e) {
        console.warn("Marca d'água não carregada:", e);
      }

      // Geometria das molduras
      const OX = 8,
        OY = 8,
        OW = 281,
        OH = 194; // externa (1px preta)
      const IX = 12,
        IY = 12,
        IW = 273,
        IH = 186; // interna (5px bordô)

      const drawWatermark = () => {
        if (!bgImg) return;
        const hasGState = typeof doc.GState === "function";
        if (!hasGState) {
          console.warn("GState indisponível; marca d'água omitida.");
          return;
        }
        try {
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: 0.11 }));
          drawFitted(doc, bgImg, IX + 6, IY + 6, IW - 12, IH - 12, "JPEG");
          doc.restoreGraphicsState();
        } catch (e) {
          console.warn("Falha ao desenhar marca d'água:", e);
          try {
            doc.restoreGraphicsState();
          } catch (_) {}
        }
      };

      const paintPageChrome = () => {
        drawWatermark();
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(OX, OY, OW, OH);
        doc.setDrawColor(...C_MAROON);
        doc.setLineWidth(1.5);
        doc.rect(IX, IY, IW, IH);
      };

      // ===================== PÁGINA 1 =====================
      paintPageChrome();

      // Cabeçalho (duas linhas, bordô, negrito)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(...C_MAROON);
      doc.text("CENTRO ACADÊMICO", cx, 34, { align: "center" });
      doc.text("EMÍLIO RIBAS", cx, 46, { align: "center" });

      // Corpo (centralizado, sem negrito) — replica o texto do template clássico
      let body = `Certificamos que ${holder.name}, CPF ${holder.cpf}, participou do ${course.name},`;
      body += course.ending_date
        ? ` de ${formatDate(course.beginning_date)} até ${formatDate(course.ending_date)}`
        : ` em ${formatDate(course.beginning_date)}`;
      body += `, `;
      body +=
        courseUser && courseUser.user_quality
          ? `na qualidade de ${courseUser.user_quality}`
          : ``;
      body += `, organizado pelo ${course.responsibleBody}, vinculado ao Curso de Medicina do Centro Universitário Padre Albino(FAMECA/UNIFIPA)`;
      body += workload !== "" ? `, com carga horária de ${workload} horas` : ``;
      body += `.`;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(13);
      doc.setTextColor(...C_BODY);
      const bodyLines = doc.splitTextToSize(body, 205);
      doc.text(bodyLines, cx, 72, { align: "center", lineHeightFactor: 1.6 });

      // ---- Pré-carrega imagens de assinatura (carregador resistente a CORS) ----
      const sigImgs = {};
      for (let i = 0; i < s.length; i++) {
        if (s[i]) {
          try {
            sigImgs[i] = await loadRemoteImage(s[i].image_path);
          } catch (e) {
            console.warn(
              "Assinatura não carregada (verifique CORS do endpoint de assinaturas):",
              s[i].image_path,
              e,
            );
          }
        }
      }

      // ---- Distribuição das assinaturas (replica a lógica do DOM) ----
      const threeSig = s[0] && s[1] && s[2] && !s[3];
      let leftCluster = [];
      let rightCluster = [];
      if (threeSig) {
        leftCluster = [
          { i: 0, up: false },
          { i: 1, up: false },
        ];
        rightCluster = [{ i: 2, up: false }];
      } else {
        if (s[0]) leftCluster.push({ i: 0, up: false });
        if (s[2]) leftCluster.push({ i: 2, up: true });
        if (s[3]) rightCluster.push({ i: 3, up: true });
        if (s[1]) rightCluster.push({ i: 1, up: false });
      }

      const SIG_BOX_W = 34;
      const SIG_BOX_H = 15;
      const SIG_GAP = 6;
      const SIG_LOWER_TOP = 158;
      const SIG_RISE = 38;
      const RIGHT_EDGE = 271;

      const renderCluster = (cluster, anchor) => {
        if (!cluster.length) return;
        const totalW =
          cluster.length * SIG_BOX_W + (cluster.length - 1) * SIG_GAP;
        let sx = anchor === "left" ? 26 : RIGHT_EDGE - totalW;
        for (const e of cluster) {
          const imgTop = e.up ? SIG_LOWER_TOP - SIG_RISE : SIG_LOWER_TOP;
          if (sigImgs[e.i]) {
            try {
              drawFitted(doc, sigImgs[e.i], sx, imgTop, SIG_BOX_W, SIG_BOX_H);
            } catch (err) {
              console.warn(
                "Não foi possível desenhar a assinatura (canvas tainted?):",
                err,
              );
            }
          }
          const lineY = imgTop + SIG_BOX_H + 1;
          doc.setDrawColor(...C_SIGN);
          doc.setLineWidth(0.3);
          doc.line(sx + 2, lineY, sx + SIG_BOX_W - 2, lineY);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...C_SIGN);
          doc.text(s[e.i].name || "", sx + SIG_BOX_W / 2, lineY + 4, {
            align: "center",
            maxWidth: SIG_BOX_W,
          });
          sx += SIG_BOX_W + SIG_GAP;
        }
      };

      renderCluster(leftCluster, "left");
      renderCluster(rightCluster, "right");

      // ---- Logos (centralizadas no rodapé) ----
      if (logos && logos.length) {
        const LOGO = 22;
        const LGAP = 10;
        const totalW = logos.length * LOGO + (logos.length - 1) * LGAP;
        let lx = cx - totalW / 2;
        const lTop = 168;
        for (const logo of logos) {
          try {
            const img = await loadRemoteImage(logo.image_path);
            drawFitted(doc, img, lx, lTop, LOGO, LOGO);
          } catch (e) {
            console.warn("Logo não carregada (CORS?):", logo.image_path, e);
          }
          lx += LOGO + LGAP;
        }
      }

      // ===================== PÁGINA 2 =====================
      doc.addPage();
      paintPageChrome();

      if (leftTopics.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text("Tópicos Abordados", cx, 30, { align: "center" });
      }

      // Tópicos em duas colunas (monoespaçado) + divisória central
      if (leftTopics.length || rightTopics.length) {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(cx, 38, cx, 148); // divisória vertical

        doc.setFont("courier", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...C_TEXT_GRAY);
        const topicsTop = 44;
        const step = 6;
        leftTopics.forEach((topic, i) => {
          doc.text(String(topic), 22, topicsTop + i * step);
        });
        rightTopics.forEach((topic, i) => {
          doc.text(String(topic), 152, topicsTop + i * step);
        });
      }

      // ---- QR (reaproveita o <QRCodeSVG> já renderizado no DOM) + títulos ----
      const qrSize = 30;
      const qrX = cx - qrSize / 2;
      const qrY = 150;
      const qrSvg = document.querySelector("#divToPrint .qrCode svg");
      if (qrSvg) {
        await svg2pdf(qrSvg, doc, {
          x: qrX,
          y: qrY,
          width: qrSize,
          height: qrSize,
        });
      } else {
        console.warn("SVG do QR não encontrado no DOM.");
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...C_TEXT_GRAY);
      doc.text(
        "Emitido pelo Centro Acadêmico Emílio Ribas.",
        cx,
        qrY + qrSize + 6,
        { align: "center" },
      );
      doc.text("Verifique a autenticidade no QR Code", cx, qrY + qrSize + 11, {
        align: "center",
      });

      doc.save(`Certificado_${holder.name}.pdf`);
    } catch (error) {
      console.error("Falha ao gerar o PDF:", error);
      alert("Ocorreu um erro ao gerar o PDF. Tente novamente.");
    }
  }

  //1) Enquanto carrega, mostra o loader
  if (loading) {
    return (
      <div className="loaderDiv">
        {" "}
        <Loader type="ThreeDots" color={"#863231"} />{" "}
      </div>
    );
  }

  //2) Erro (token invalido, inexistente ou certificado indisponivel)
  if (errorMessage || !data) {
    return (
      <div className="certification">
        <div className="pageTitle">
          <span>Certificado</span>
        </div>
        <div className="pageContent">
          <p style={{ textAlign: "center", padding: "40px" }}>{errorMessage}</p>
        </div>
      </div>
    );
  }

  const { course, holder, courseUser, signatures, logos, workload, isOwner } =
    data;

  //3) Template moderno
  if (course && course.certificate_template === 2) {
    return (
      <CertificationModern
        course={course}
        holder={holder}
        courseUser={courseUser}
        signatures={signatures}
        logos={logos}
        leftTopics={leftTopics}
        rightTopics={rightTopics}
        workload={workload}
        url={url}
        formatDate={formatDate}
        isOwner={isOwner}
      />
    );
  }

  //4) Template classico
  return (
    <div className="certification">
      <div className="pageTitle">
        <span>Certificado</span>
      </div>
      <div id="divToPrint">
        <div className="certificationContent commonWidth">
          <div className="certificationBorder">
            <div className="top">
              <span>CENTRO ACADÊMICO </span>
              <span>EMÍLIO RIBAS</span>
              <span className="certificationText">
                Certificamos que {holder.name}, CPF {holder.cpf}, participou do{" "}
                {course.name},
                {course.ending_date
                  ? " de " +
                    formatDate(course.beginning_date) +
                    " até " +
                    formatDate(course.ending_date)
                  : " em " + formatDate(course.beginning_date)}
                ,{" "}
                {courseUser && courseUser.user_quality
                  ? `na qualidade de ${courseUser.user_quality}`
                  : ""}
                , organizado pelo {course.responsibleBody}, vinculado ao Curso
                de Medicina do Centro Universitário Padre Albino(FAMECA/UNIFIPA)
                {workload !== ""
                  ? `, com carga horária de ${workload} horas`
                  : ""}
                .{" "}
              </span>
            </div>

            {signatures[0] &&
            signatures[1] &&
            signatures[2] &&
            !signatures[3] ? (
              <div
                className="bottomLeft"
                style={{
                  left: !signatures[2] && !signatures[3] ? "150px" : "20px",
                }}
              >
                <div className="signature">
                  <img
                    className="signatureImg"
                    alt="signature"
                    src={signatures[0].image_path}
                  />
                  <label>{signatures[0].name}</label>
                </div>
                <div className="signature">
                  <img
                    className="signatureImg"
                    alt="signature"
                    src={signatures[1].image_path}
                  />
                  <label>{signatures[1].name}</label>
                </div>
              </div>
            ) : null}
            {!(
              signatures[0] &&
              signatures[1] &&
              signatures[2] &&
              !signatures[3]
            ) ? (
              <div
                className="bottomLeft"
                style={{
                  left: !signatures[2] && !signatures[3] ? "150px" : "20px",
                }}
              >
                {signatures[0] ? (
                  <div className="signature">
                    <img
                      className="signatureImg"
                      alt="signature"
                      src={signatures[0].image_path}
                    />
                    <label>{signatures[0].name}</label>
                  </div>
                ) : null}
                {signatures[2] ? (
                  <div className="signature up">
                    <img
                      className="signatureImg"
                      alt="signature"
                      src={signatures[2].image_path}
                    />
                    <label>{signatures[2].name}</label>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="bottomMiddle">
              <div className="logos">
                {logos.map((logo) => {
                  return (
                    <img
                      className="logo"
                      alt="logo"
                      src={logo.image_path}
                      key={"logo" + logo.id}
                    />
                  );
                })}
              </div>
            </div>
            {signatures[0] &&
            signatures[1] &&
            signatures[2] &&
            !signatures[3] ? (
              <div
                className="bottomRight"
                style={{ right: !signatures[3] ? "150px" : "20px" }}
              >
                <div className="signature">
                  <img
                    className="signatureImg"
                    alt="signature"
                    src={signatures[2].image_path}
                  />
                  <label>{signatures[2].name}</label>
                </div>
              </div>
            ) : null}
            {!(
              signatures[0] &&
              signatures[1] &&
              signatures[2] &&
              !signatures[3]
            ) ? (
              <div
                className="bottomRight"
                style={{
                  right: !signatures[2] && !signatures[3] ? "150px" : "20px",
                }}
              >
                {signatures[3] ? (
                  <div className="signature up">
                    <img
                      className="signatureImg"
                      alt="signature"
                      src={signatures[3].image_path}
                    />
                    <label>{signatures[3].name}</label>
                  </div>
                ) : null}
                {signatures[1] ? (
                  <div className="signature">
                    <img
                      className="signatureImg"
                      alt="signature"
                      src={signatures[1].image_path}
                    />
                    <label>{signatures[1].name}</label>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="certificationContent commonWidth">
          <div className="certificationBorder">
            {leftTopics.length > 0 ? (
              <h3 style={{ textAlign: "center" }}>Tópicos Abordados</h3>
            ) : null}
            <div className="topics monospace">
              <div className="left">
                {leftTopics.map((topic, i) => {
                  return (
                    <div className="topic" key={"left" + i}>
                      <span>{topic}</span>
                    </div>
                  );
                })}
              </div>
              <div className="right">
                {rightTopics.map((topic, i) => {
                  return (
                    <div className="topic" key={"right" + i}>
                      <span>{topic}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="middle">
              <div className="qrCode">
                <QRCodeSVG value={url} size={150} />
              </div>
              <div className="qrTitle">
                <span>Emitido pelo Centro Acadêmico Emílio Ribas.</span>
                <span>Verifique a autenticidade no QR Code</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isOwner ? (
        <button id="printButton" onClick={printDocument}>
          Download PDF
        </button>
      ) : null}
    </div>
  );
}
